import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import Container from '@mui/material/Container';
import {
  accountApis,
  useGetClinicAccountsQuery,
} from '../../../app/services/account';
import { useToast } from '../../../app/toast';
import Loading from '../../../common/components/Loading';
import PageHeader from '../../../common/components/PageHeader';
import RoundedContainer from '../../../common/components/RoundedContainer';
import DashboardLayout from '../../../common/layouts/Dashboard';
import { addBreadcrumb, BreadcrumbType } from '../../../common/utils/editBreadcrums';
import { getPageTitle } from '../../../common/utils/pageUtils';
import { PageProps } from '../../../types/PageProps';
import { ImportPatientData } from '../../../types/PatientRequest';
import { ImportPatientError } from '../../../types/ResponseError';
import { UploadUrlResponse } from '../../../types/UploadUrl';
import ImportPatientForm from './ImportPatientForm';
import LineErrorTable from './LineErrorTable';
import { useAppDispatch } from '../../../app/hooks';
import { ImportFolderType } from '../../../common/utils/importFolder';

export default function ImportPatientPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [errorLineItems, setErrorLineItems] = useState<{ [key: string]: string[] }[]>([]);
  const dispatch = useAppDispatch();

  const { data: accountData, isLoading: loadAccountsData } = useGetClinicAccountsQuery();

  const validationSchema = yup
    .object({
      clinicAccount: yup.string().required(t('user.manageUsers.error.blankClinicAccount')),
    })
    .required();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ImportPatientData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      clinicAccount: '',
      file: undefined,
    },
  });

  const { state } = useLocation();
  const { navBackUrl } = !!state && state;
  const newBreadcrumb: BreadcrumbType = {
    text: t('dashboard.accountStats.totalPatients.title'),
    link: navBackUrl || `/dashboard/patients`,
  };

  const handlePageError = (pageErrors: string[]) => {
    setPageErrors(pageErrors);
  };

  const handleErrorLineItems = (errorItems: { [key: string]: string[] }[]) => {
    setErrorLineItems(errorItems);
  };

  const onSubmit = async (data: ImportPatientData) => {
    const { file } = data;

    if (!file) {
      setPageErrors([t('user.manageUsers.error.blankImportFile')]);
      return;
    }

    try {
      // Get presigned URL from S3
      const uploadUrlResult = await dispatch(
        accountApis.getUploadUrl.initiate({
          clinicAccountId: data.clinicAccount,
          fileName: file.name,
          contentType: file.type,
          key: ImportFolderType.Patient,
        }, { forceRefetch: true })
      );

      const uploadUrlData: UploadUrlResponse | undefined = uploadUrlResult?.data;

      if (!uploadUrlData || !uploadUrlData.url || !uploadUrlData.key) {
        throw new Error('Failed to get upload URL');
      }

      const { url } = uploadUrlData;

      // Upload file to S3
      axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
        withCredentials: false,
        timeout: 10000,
      });

      setPageErrors([]);
      setErrorLineItems([]);
      toast.publish(t('patient.importPatient.processingMessage'), 'success');
    } catch (e: any) {
      // Handle errors from either S3 upload or import API
      if (e?.data?.error || e?.data?.errorImportLines) {
        const {
          data: { error, errorImportLines },
        } = e as ImportPatientError;
        if (error) {
          setPageErrors(error);
        }
        if (errorImportLines) {
          setErrorLineItems(errorImportLines);
        }
      } else {
        // Handle S3 upload errors or other errors
        setPageErrors([e?.message || t('patient.importPatient.uploadError')]);
      }
    }
  };

  return (
    <DashboardLayout breadcrumbs={addBreadcrumb(breadcrumbs!, newBreadcrumb)}>
      <Helmet>
        <title>{getPageTitle(t('patient.importPatient.title'))}</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader
          sx={{ mb: 2 }}
          headerText={t('patient.importPatient.title')}
          backUrl={navBackUrl || `/dashboard/patients`}
        />
      </Container>

      {loadAccountsData ? (
        <Loading />
      ) : (
        <RoundedContainer>
          <ImportPatientForm
            control={control}
            setValue={setValue}
            errors={errors}
            setPageErrors={handlePageError}
            pageErrors={pageErrors}
            handleSubmit={handleSubmit(onSubmit)}
            clinicAccounts={accountData?.clinicAccounts}
            setErrorLineItems={handleErrorLineItems}
            hasErrorLineItems={errorLineItems.length > 0}
          />
        </RoundedContainer>
      )}

      {errorLineItems.length > 0 && (
        <RoundedContainer sx={{ my: 2, py: 3 }}>
          <LineErrorTable errorLineItems={errorLineItems} />
        </RoundedContainer>
      )}
    </DashboardLayout>
  );
}
